import { useState, useCallback, useMemo, useEffect } from "react";
import { ContentLog } from "../types/dashboard";
import { SOCIAL_PLATFORMS } from "../utils/platform-post-util";
import useBrandHistoryApi from "../api/useBrandHistoryApi";
import { BRAND_HISTORY_REFRESH_EVENT } from "../utils/brand-history-events";
import useWeeklyPlanApi from "../api/use-weekly-plan-api";
import { WeeklyPlanItem } from "../types/weekly-plan";
import { formatWeekStart } from "../utils/week-plan-date";
import { getContentLogCalendarDate } from "../utils/content-log-date";
import { WEEKLY_PLAN_REFRESH_EVENT } from "../utils/weekly-plan-events";

export interface CalendarDay {
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  date: Date;
}

const PLATFORM_SHORT_LABELS: Record<string, string> = {
  linkedin: "LI",
  linkedIn: "LI",
  x: "X",
  twitter: "X",
  facebook: "FB",
  instagram: "IG",
  reddit: "RD",
};

const OPEN_WEEKLY_PLAN_STATUSES = new Set(["planned", "drafted"]);

const isOpenWeeklyPlanItem = (item: WeeklyPlanItem) =>
  OPEN_WEEKLY_PLAN_STATUSES.has(item.status);

export function useContentCalendar(brandId: string) {
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [logs, setLogs] = useState<ContentLog[]>([]);
  const [plannedItems, setPlannedItems] = useState<WeeklyPlanItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { getBrandHistoryByMonth } = useBrandHistoryApi();
  const { getWeeklyPlan } = useWeeklyPlanApi();

  const fetchLogs = useCallback(
    async (year: number, month: number, reconcile: boolean = false) => {
      if (!brandId) return;
      setLoading(true);
      try {
        const data = await getBrandHistoryByMonth(
          brandId,
          month + 1,
          year,
          reconcile
        );
        setLogs(data);
      } finally {
        setLoading(false);
      }
    },
    [brandId, getBrandHistoryByMonth]
  );

  const fetchPlannedItems = useCallback(
    async (days: CalendarDay[]) => {
      if (!brandId) return;
      const weekStarts = Array.from(
        new Set(
          days
            .filter((day) => day.isCurrentMonth)
            .map((day) => formatWeekStart(day.date))
        )
      );
      const plans = await Promise.all(
        weekStarts.map((weekStart) => getWeeklyPlan(brandId, weekStart))
      );
      setPlannedItems(
        plans
          .flatMap((plan) => plan?.items ?? [])
          .filter(isOpenWeeklyPlanItem)
      );
    },
    [brandId, getWeeklyPlan]
  );

  useEffect(() => {
    fetchLogs(currentYear, currentMonth);
  }, [currentYear, currentMonth, fetchLogs]);

  useEffect(() => {
    if (typeof window === "undefined" || !brandId) {
      return;
    }

    const handleRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ brandId?: string }>).detail;
      if (detail?.brandId !== brandId) {
        return;
      }

      fetchLogs(currentYear, currentMonth, false);
    };

    window.addEventListener(BRAND_HISTORY_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(BRAND_HISTORY_REFRESH_EVENT, handleRefresh);
    };
  }, [brandId, currentMonth, currentYear, fetchLogs]);

  const goToPrevMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 0) {
        setCurrentYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
    setSelectedDate(null);
  }, []);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth((m) => {
      if (m === 11) {
        setCurrentYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
    setSelectedDate(null);
  }, []);

  const calendarDays = useMemo((): CalendarDay[] => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();
    const today = new Date();

    const days: CalendarDay[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      days.push({
        day,
        isCurrentMonth: false,
        isToday: false,
        date: new Date(currentYear, currentMonth - 1, day),
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, currentMonth, d);
      days.push({
        day: d,
        isCurrentMonth: true,
        isToday:
          d === today.getDate() &&
          currentMonth === today.getMonth() &&
          currentYear === today.getFullYear(),
        date,
      });
    }

    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({
          day: i,
          isCurrentMonth: false,
          isToday: false,
          date: new Date(currentYear, currentMonth + 1, i),
        });
      }
    }

    return days;
  }, [currentMonth, currentYear]);

  useEffect(() => {
    void fetchPlannedItems(calendarDays);
  }, [calendarDays, fetchPlannedItems]);

  useEffect(() => {
    if (typeof window === "undefined" || !brandId) {
      return;
    }

    const handleRefresh = (event: Event) => {
      const detail = (event as CustomEvent<{ brandId?: string }>).detail;
      if (detail?.brandId !== brandId) {
        return;
      }

      void fetchPlannedItems(calendarDays);
    };

    window.addEventListener(WEEKLY_PLAN_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(WEEKLY_PLAN_REFRESH_EVENT, handleRefresh);
    };
  }, [brandId, calendarDays, fetchPlannedItems]);

  const getLogsForDay = useCallback(
    (date: Date): ContentLog[] => {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return logs.filter((log) => {
        const calendarDate = getContentLogCalendarDate(log);
        return (
          calendarDate !== null &&
          calendarDate >= dayStart &&
          calendarDate <= dayEnd
        );
      });
    },
    [logs]
  );

  const selectedDayLogs = useMemo(() => {
    if (!selectedDate) return [];
    return getLogsForDay(selectedDate);
  }, [selectedDate, getLogsForDay]);

  const getPlannedItemsForDay = useCallback(
    (date: Date): WeeklyPlanItem[] => {
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      return plannedItems.filter((item) => {
        const plannedFor = new Date(item.planned_for);
        return plannedFor >= dayStart && plannedFor <= dayEnd;
      });
    },
    [plannedItems]
  );

  const selectedDayPlannedItems = useMemo(() => {
    if (!selectedDate) return [];
    return getPlannedItemsForDay(selectedDate);
  }, [selectedDate, getPlannedItemsForDay]);

  const getPlatformIcon = useCallback((platformId: string) => {
    return SOCIAL_PLATFORMS.find((p) => p.id === platformId)?.icon ?? null;
  }, []);

  const getPlatformLabel = useCallback((platformId: string) => {
    return PLATFORM_SHORT_LABELS[platformId] ?? platformId;
  }, []);

  const getPlatformName = useCallback((platformId: string) => {
    return (
      SOCIAL_PLATFORMS.find((p) => p.id === platformId)?.name ?? platformId
    );
  }, []);

  const formatTime = useCallback((isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, []);

  const monthLabel = useMemo(() => {
    return new Date(currentYear, currentMonth).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }, [currentMonth, currentYear]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return "";
    return selectedDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }, [selectedDate]);

  return {
    currentMonth,
    currentYear,
    monthLabel,
    calendarDays,
    selectedDate,
    setSelectedDate,
    selectedDayLogs,
    selectedDateLabel,
    goToPrevMonth,
    goToNextMonth,
    getLogsForDay,
    getPlannedItemsForDay,
    getPlatformIcon,
    getPlatformLabel,
    getPlatformName,
    formatTime,
    loading,
    fetchLogs,
    plannedItems,
    selectedDayPlannedItems,
    fetchPlannedItems,
  };
}
